// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect } from 'react'
import type { MutableRefObject } from 'react'
import { Briefcase } from 'lucide-react'
import { InfoButton } from '@/components/ui/InfoButton'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { YesNoStep } from './YesNoStep'
import { BusinessPage } from '@/pages/business/BusinessPage'
import { useWizardStore } from '@/store'
import type { SaveStatus } from '@/hooks/useAutosave'

export function NewStep06Business({ onSaveStatus, saveRef }: { onSaveStatus: (s: SaveStatus) => void; saveRef?: MutableRefObject<(() => Promise<void>) | null> }) {
  const { answers, setAnswer } = useWizardStore()
  const value = answers.hasBusiness === true ? 'yes' : answers.hasBusiness === false ? 'no' : ''
  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  function handleChange(v: string) {
    setAnswer('hasBusiness', v === 'yes')
    onSaveStatus('saved')
    setTimeout(() => onSaveStatus('idle'), 1500)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <InfoButton title="Onderneming"
          content={<>
            <p>Leg hier de basisgegevens van uw onderneming vast zodat uw naasten weten wat er geregeld moet worden.</p>
            <p><strong>KvK-nummer:</strong> het inschrijfnummer bij de Kamer van Koophandel. Handig voor uw naasten bij afwikkeling.</p>
            <p><strong>Boekhouder:</strong> noteer de naam en het contactgegeven van uw boekhouder of accountant - die kent de financiële situatie.</p>
            <p><strong>Wens voor opvolging:</strong> wilt u dat de onderneming wordt voortgezet, overgedragen of afgewikkeld?</p>
          </>} />
      </div>
      <InfoBlock title="Goed om te weten - onderneming" variant="blue" defaultOpen={false}>
        <p>Als ondernemer bent u vaak de spil. Valt u weg, dan kan er van alles stilvallen: betalingen, opdrachten, soms het hele bedrijf. Vraag uzelf af wie er bij de zakelijke rekening kan en wie namens het bedrijf mag tekenen.</p>
        <p>Een paar dingen die snel spelen: de inschrijving bij de <strong>Kamer van Koophandel</strong> moet worden aangepast of uitgeschreven, een zakelijke rekening kan geblokkeerd raken, en het helpt als genoteerd is wie uw boekhouder of accountant is.</p>
                                <p>WilsApp legt uw wensen vast als persoonlijke leidraad. De officiële afhandeling (KvK, notaris, bank) verloopt na overlijden via de bevoegde instanties.</p>
      </InfoBlock>
      <YesNoStep
        title="Onderneming"
        question="Heeft u een onderneming, zzp of bedrijf?"
        description="Als eenmanszaak, vof, bv of een andere rechtsvorm."
        value={value}
        onChange={handleChange}
        conditionKey="yes"
        conditionContent={
          <div className="flex items-start gap-3 bg-white rounded-2xl border border-sage-200 p-4">
            <Briefcase className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-ink mb-1.5">Leg dit vast voor uw naasten</p>
              <ul className="text-xs text-ink-3 leading-relaxed space-y-1.5">
                <li>• <strong>Bedrijfsnaam en KvK-nummer</strong> - noteer de naam en het inschrijfnummer</li>
                <li>• <strong>Rechtsvorm</strong> - eenmanszaak, vof, bv of anders</li>
                <li>• <strong>Boekhouder/accountant</strong> - naam en contactgegevens</li>
                <li>• <strong>Zakelijke bankrekening</strong> - bij welke bank en welk rekeningnummer</li>
                <li>• <strong>Administratielocatie</strong> - waar zijn de zakelijke administratie en contracten?</li>
                <li>• <strong>Belangrijke klanten en leveranciers</strong> - wie zijn de contactpersonen?</li>
                <li>• <strong>Wens voor opvolging</strong> - wilt u dat de zaak wordt voortgezet, overgedragen of afgewikkeld?</li>
              </ul>
            </div>
          </div>
        }
      />

      {answers.hasBusiness === true && (
        <BusinessPage saveRef={saveRef} />
      )}
    </div>
  )
}
